<script setup lang="ts">
import { onMounted, ref, defineComponent, createCommentVNode } from 'vue'
import { useI18n } from 'vue-i18n'

import { AddJavascriptToHeader, SetIsLoading } from '@/services/AoeHelpers'
import { CartPaymentTokenCreate, CartSubmit } from '@/api/Cart'
import { ApiPost } from '@/api/Common'
import { GetImageRootUrl, GetOrderEntryConnectorUrl } from '@/stores/SettingsStore'
import { ShippingAddressAddToCart, ShippingMethodAddToCart, SaveCartToLocalStorage, GetCurrentCartFromLocalStorage } from '@/services/CartService'
import type { IAoeCart, ICartResponse, IShippingAddress } from '@/types/CartTypes'
import type { AxiosError } from 'axios'
import { useLogger } from 'vue-logger-plugin'
import { handleError } from '@/services/ErrorService'

import type { IBraintree } from '@/types/PaymentGatewayTypes'

declare const braintree: IBraintree

type ApplePayTokenizePayload = Record<string, unknown>

const { t } = useI18n()
const logger = useLogger()

const props = defineProps<{
  cart: IAoeCart
  systemKeyword: string
  parameters: {
    braintreeMerchantId: string
    displayName: string
  }
}>()

const emit = defineEmits<{
  submitted: [response: unknown]
  error: [error: unknown]
}>()

const applePayCheckoutImageUrl = ref('')
const isSupported = ref(false)
const initError = ref('')

let applePayInstance: any | null = null
let clientToken: string | null = null

const BraintreeApplePayExpressNotReadyComment = defineComponent({
  name: 'BraintreeApplePayExpressNotReadyComment',
  props: {
    isSupported: { type: Boolean, required: true },
    initError: { type: String, required: true },
  },
  setup(p) {
    let hasLogged = false
    return () => {
      const message = `BraintreeApplePayExpressCheckoutEntry not ready: isSupported=${String(
        p.isSupported,
      )}, initError=${String(p.initError)}`

      if (!hasLogged) {
        hasLogged = true
        logger.debug('BraintreeApplePayExpressCheckoutEntry', message)
      }

      return createCommentVNode(message)
    }
  },
})

function postPaymentError(error: unknown) {
  const axiosError = error as AxiosError
  const messageData = axiosError?.response?.data ?? null
  window.postMessage({ messageType: 'aoepaymentcreationerror', messageData })
}

function getChargeAmount(cart: IAoeCart): number {
  if (typeof cart.balance === 'number') return Number(cart.balance)
  return Number(cart.total)
}

function mapApplePayContactToShippingAddress(contact: any): IShippingAddress {
  const postal = contact?.postalAddress ?? null

  return {
    firstName: String(contact?.givenName ?? ''),
    lastName: String(contact?.familyName ?? ''),
    addressLine1: String(postal?.street ?? ''),
    addressLine2: postal?.subLocality ? String(postal.subLocality) : null,
    houseNumber: null,
    city: String(postal?.city ?? ''),
    stateCode: postal?.administrativeArea ? String(postal.administrativeArea) : null,
    postalCode: String(postal?.postalCode ?? ''),
    countryCode: String(postal?.countryCode ?? ''),
  }
}

function buildApplePayShippingMethods(cart: IAoeCart): any[] {
  const methods = cart.availableShippingMethods ?? []
  return methods.map(m => ({
    identifier: m.shippingMethodName,
    label: m.shippingMethodFriendlyName,
    detail: '',
    amount: Number(m.shippingCost).toFixed(2),
  }))
}

function getSelectedShippingMethodIdentifier(cart: IAoeCart): string | null {
  const current = cart.shippingMethod?.name
  if (current) return current
  const first = cart.availableShippingMethods?.[0]?.shippingMethodName
  return first ?? null
}

function buildPaymentRequest(cart: IAoeCart): any {
  const shippingMethods = buildApplePayShippingMethods(cart)
  const selectedIdentifier = getSelectedShippingMethodIdentifier(cart)

  const total = {
    label: props.parameters.displayName,
    amount: getChargeAmount(cart).toFixed(2),
  }

  return {
    total,
    requiredBillingContactFields: ['postalAddress', 'name', 'email', 'phone'],
    requiredShippingContactFields: ['postalAddress', 'name', 'email', 'phone'],
    shippingMethods,
    shippingMethod: shippingMethods.find(m => m.identifier === selectedIdentifier) ?? null,
  }
}

async function createPayment(
  payload: ApplePayTokenizePayload,
  billingContact?: unknown,
  shippingContact?: unknown,
) {
  const createPaymentUrl = await GetOrderEntryConnectorUrl('CartPaymentCreate')

  const braintreeApplePayTokenizeResponse: Record<string, unknown> = {
    ...payload,
    clientToken,
  }

  if (billingContact !== undefined || shippingContact !== undefined) {
    const payment: Record<string, unknown> = {}

    if (billingContact !== undefined) {
      payment.billingContact = billingContact
    }

    if (shippingContact !== undefined) {
      payment.shippingContact = shippingContact
    }

    braintreeApplePayTokenizeResponse.payment = payment
  }

  const jsonDataToSave = {
    systemKeyword: props.systemKeyword,
    aOECartIdToken: props.cart.aoeCartIdToken,
    BraintreeApplePayTokenizeResponse: braintreeApplePayTokenizeResponse,
    charge: Number(getChargeAmount(props.cart)),
  }

  return ApiPost(createPaymentUrl, jsonDataToSave)
    .then((result: unknown) => {
      window.postMessage({ messageType: 'aoepaymentcreated', messageData: result })
      return result
    })
    .catch((error: unknown) => {
      postPaymentError(error)
      throw error
    })
}

async function initializeIfNeeded() {
  if (initError.value) return
  if (applePayInstance && clientToken) return

  if (typeof window === 'undefined') return

  try {
    const imageRootUrl = await GetImageRootUrl()
    applePayCheckoutImageUrl.value =
      imageRootUrl + '/contentimages/Apple_Pay_Mark_RGB_041619.svg'

    const ApplePaySessionCtor = (window as any).ApplePaySession
    isSupported.value =
      !!ApplePaySessionCtor &&
      typeof ApplePaySessionCtor.canMakePayments === 'function' &&
      !!ApplePaySessionCtor.canMakePayments()

    if (!isSupported.value) return

    await Promise.all([
      AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/client.min.js'),
      AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/apple-pay.min.js'),
    ])

    logger.debug('BraintreeApplePayExpressCheckoutEntry', 'CartPaymentTokenCreate: start')
    const response = await CartPaymentTokenCreate({
      aoeCartIdToken: props.cart.aoeCartIdToken,
      systemKeyword: props.systemKeyword,
      charge: getChargeAmount(props.cart),
      tokenType: 'applepay',
    })
    logger.debug('BraintreeApplePayExpressCheckoutEntry', 'CartPaymentTokenCreate: done', response)

    if (!response?.item?.ClientToken) {
      initError.value = t('cart.NotSupported')
      return
    }

    clientToken = response.item.ClientToken

    await new Promise<void>((resolve, reject) => {
      braintree.client.create(
        { authorization: clientToken },
        (clientErr: any, clientInstance: any) => {
          if (clientErr) {
            reject(clientErr)
            return
          }

          ;(braintree as any).applePay.create(
            {
              client: clientInstance,
              merchantIdentifier: props.parameters.braintreeMerchantId,
            },
            (applePayErr: any, instance: any) => {
              if (applePayErr) {
                reject(applePayErr)
                return
              }
              applePayInstance = instance
              resolve()
            },
          )
        },
      )
    })
  } catch (error) {
    logger.error('BraintreeApplePayExpressCheckoutEntry', 'Apple Pay init failed', error)
    initError.value = t('cart.NotSupported')
  }
}

async function startExpressCheckout() {
  await initializeIfNeeded()

  if (!isSupported.value || initError.value || !applePayInstance || !clientToken) {
    return
  }

  const ApplePaySessionCtor = (window as any).ApplePaySession
  if (!ApplePaySessionCtor) return

  SetIsLoading(true)

  try {
    const paymentRequest = applePayInstance.createPaymentRequest(buildPaymentRequest(props.cart))
    const session = new ApplePaySessionCtor(3, paymentRequest)

    session.onshippingcontactselected = async (event: any) => {
      try {
        const shippingAddress = mapApplePayContactToShippingAddress(event?.shippingContact)

        await ShippingAddressAddToCart({
          aoeCartIdToken: props.cart.aoeCartIdToken,
          address: shippingAddress,
          skipAddressValidation: true,
        })

        const updatedCart = GetCurrentCartFromLocalStorage() ?? props.cart
        const updatedRequest = applePayInstance.createPaymentRequest(buildPaymentRequest(updatedCart))

        const updatedShippingMethods = updatedRequest.shippingMethods ?? buildApplePayShippingMethods(updatedCart)
        const updatedTotal = updatedRequest.total ?? {
          label: props.parameters.displayName,
          amount: getChargeAmount(updatedCart).toFixed(2),
        }

        session.completeShippingContactSelection(
          ApplePaySessionCtor.STATUS_SUCCESS,
          updatedShippingMethods,
          updatedTotal,
          [],
        )
      } catch (error) {
        logger.error('BraintreeApplePayExpressCheckoutEntry', 'shippingContactSelected failed', error)
        session.completeShippingContactSelection(ApplePaySessionCtor.STATUS_FAILURE, [], paymentRequest.total, [])
        void handleError(error, t)
      }
    }

    session.onshippingmethodselected = async (event: any) => {
      try {
        const identifier = event?.shippingMethod?.identifier
        if (identifier) {
          await ShippingMethodAddToCart({
            aoeCartIdToken: props.cart.aoeCartIdToken,
            shippingMethodName: identifier,
          })
        }

        const updatedCart = GetCurrentCartFromLocalStorage() ?? props.cart
        const updatedRequest = applePayInstance.createPaymentRequest(buildPaymentRequest(updatedCart))

        const updatedTotal = updatedRequest.total ?? {
          label: props.parameters.displayName,
          amount: getChargeAmount(updatedCart).toFixed(2),
        }

        session.completeShippingMethodSelection(
          ApplePaySessionCtor.STATUS_SUCCESS,
          updatedTotal,
          [],
        )
      } catch (error) {
        logger.error('BraintreeApplePayExpressCheckoutEntry', 'shippingMethodSelected failed', error)
        session.completeShippingMethodSelection(ApplePaySessionCtor.STATUS_FAILURE, paymentRequest.total, [])
        void handleError(error, t)
      }
    }

    session.onpaymentauthorized = (event: any) => {
      applePayInstance.tokenize(
        { token: event.payment.token },
        async (err: any, payload: any) => {
          if (err) {
            logger.error('BraintreeApplePayExpressCheckoutEntry', 'Apple Pay tokenization failed', err)
            session.completePayment(ApplePaySessionCtor.STATUS_FAILURE)
            postPaymentError(err)
            SetIsLoading(false)
            return
          }

          try {
            await createPayment(
              payload as ApplePayTokenizePayload,
              event.payment.billingContact,
              event.payment.shippingContact,
            )

            const submitResponse = await CartSubmit(props.cart.aoeCartIdToken)
            logger.debug('BraintreeApplePayExpressCheckoutEntry', 'CartSubmit response', submitResponse)

            if ((submitResponse as any)?.item) {
              SaveCartToLocalStorage(submitResponse as ICartResponse)
            }

            emit('submitted', submitResponse)
            window.postMessage({ messageType: 'aoeexpresscheckoutsubmitted', messageData: submitResponse })
            session.completePayment(ApplePaySessionCtor.STATUS_SUCCESS)
          } catch (error) {
            emit('error', error)
            logger.error('BraintreeApplePayExpressCheckoutEntry', 'payment authorized flow failed', error)
            session.completePayment(ApplePaySessionCtor.STATUS_FAILURE)
            void handleError(error, t)
          } finally {
            SetIsLoading(false)
          }
        },
      )
    }

    session.oncancel = () => {
      SetIsLoading(false)
    }

    session.begin()
  } catch (error) {
    emit('error', error)
    logger.error('BraintreeApplePayExpressCheckoutEntry', 'Apple Pay session failed', error)
    postPaymentError(error)
    SetIsLoading(false)
    void handleError(error, t)
  }
}

defineExpose({
  startExpressCheckout,
})

onMounted(async () => {
  await initializeIfNeeded()
})
</script>

<template>
  <BraintreeApplePayExpressNotReadyComment
    v-if="!isSupported || !!initError"
    :isSupported="isSupported"
    :initError="initError"
  />
</template>
